// ui/src/components/tenant-admin/sections/SettlementSection.jsx

/**
 * @file SettlementSection.jsx
 * @description Plan document section for settlement configuration.
 *
 * S810 REWRITE — OI-S810-SECTION-FORM-STATE
 *   - Owns local form state via useState (no onChange to parent during editing).
 *   - Exposes getValues() via useImperativeHandle for one-PATCH save flow.
 *   - useEffect resets form only when plan.planId changes.
 *   - onChange prop REMOVED. defaultProps REMOVED.
 *
 * @module tenant-admin/sections
 * @author data-fit LLC · DecisionFlow · S775 / S810
 */

import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import PropTypes from 'prop-types';

const POLICY_OPTIONS = [
  { value: 'none',     label: 'None' },
  { value: 'true_up',  label: 'Retroactive True-Up' },
  { value: 'custom',   label: 'Custom' },
];

const FREQUENCY_OPTIONS = [
  { value: 'monthly',   label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annual',    label: 'Annual' },
  { value: 'on_demand', label: 'On-demand' },
];

const COMPARISON_OPTIONS = [
  { value: 'provisional_vs_authoritative', label: 'Provisional vs authoritative calculation run' },
  { value: 'custom',                       label: 'Custom comparison basis' },
];

const YEAR_END_OPTIONS = [
  { value: 'reset',         label: 'Reset' },
  { value: 'carry_forward', label: 'Carry forward' },
  { value: 'lump_sum',      label: 'Lump-sum reconciliation' },
];

const toLabel = (options, value, fallback = 'Not specified') =>
  options.find((o) => o.value === value)?.label ?? fallback;

function initFromSettlement(settlementConfig) {
  return {
    policy:               settlementConfig.policy               ?? 'none',
    trueUpFrequency:      settlementConfig.trueUpFrequency      ?? null,
    comparisonBasis:      settlementConfig.comparisonBasis      ?? null,
    lookbackWindowMonths: settlementConfig.lookbackWindowMonths ?? null,
    carryForwardRules:    settlementConfig.carryForwardRules    ?? {
      overpaymentRecovery:  false,
      underpaymentCatchUp:  false,
      recoveryPeriodMonths: null,
    },
    yearEndTreatment: settlementConfig.yearEndTreatment ?? null,
    customTerms:      settlementConfig.customTerms      ?? null,
  };
}

const SettlementSection = forwardRef(function SettlementSection({
  plan,
  settlementConfig,
  readOnly,
  validationErrors = {},
}, ref) {
  const [form, setForm] = useState(() => initFromSettlement(settlementConfig));

  // Reset only on plan change
  useEffect(() => {
    setForm(initFromSettlement(settlementConfig));
  }, [plan.planId]); // eslint-disable-line react-hooks/exhaustive-deps

  useImperativeHandle(ref, () => ({
    getValues() {
      return { settlementConfig: form };
    },
  }), [form]);

  // ---------------------------------------------------------------------------
  // Local handlers
  // ---------------------------------------------------------------------------
  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const setCarryForward = (field, value) => {
    setForm((prev) => ({
      ...prev,
      carryForwardRules: { ...prev.carryForwardRules, [field]: value },
    }));
  };

  const handlePolicyChange = (value) => {
    setForm((prev) => ({
      ...prev,
      policy: value,
      // Reset dependent fields when policy changes
      trueUpFrequency:      null,
      comparisonBasis:      null,
      lookbackWindowMonths: null,
      carryForwardRules:    { overpaymentRecovery: false, underpaymentCatchUp: false, recoveryPeriodMonths: null },
      yearEndTreatment:     null,
      customTerms:          null,
    }));
  };

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------
  const errorFor   = (field) => validationErrors?.[field] || null;
  const errorId    = (field) => `settlement-${field.replaceAll('.', '-')}-error`;
  const describedBy = (field) => errorFor(field) ? errorId(field) : undefined;

  const renderError = (field) =>
    errorFor(field) ? (
      <p id={errorId(field)} className="settlement-error" role="alert">
        {errorFor(field)}
      </p>
    ) : null;

  const rules = form.carryForwardRules ?? {
    overpaymentRecovery: false, underpaymentCatchUp: false, recoveryPeriodMonths: null,
  };

  return (
    <section
      className="modifier-manager-card settlement-section"
      aria-labelledby="settlement-heading"
    >
      <style>{`
        .settlement-section {
          background: rgb(var(--color-surface));
          border: 1px solid rgb(var(--color-border));
          color: rgb(var(--color-text));
          font-family: "IBM Plex Sans", sans-serif;
        }
        .settlement-section .modifier-manager-header {
          border-bottom: 1px solid rgb(var(--color-border)); padding: 16px;
        }
        .settlement-section .modifier-manager-body {
          display: flex; flex-direction: column; gap: 16px; padding: 16px;
        }
        .settlement-heading { font-size: 18px; font-weight: 600; margin: 0; }
        .settlement-subtitle, .settlement-help, .settlement-readonly-label {
          color: rgb(var(--color-text-muted)); font-size: 13px; line-height: 1.5;
        }
        .settlement-subtitle { margin: 4px 0 0; }
        .settlement-fieldset { border: 0; margin: 0; padding: 0; }
        .settlement-legend, .settlement-label {
          color: rgb(var(--color-text)); display: block; font-size: 13px;
          font-weight: 600; margin-bottom: 6px;
        }
        .settlement-policy-options {
          display: grid; gap: 8px; grid-template-columns: repeat(3, minmax(0, 1fr));
        }
        .settlement-radio-card {
          align-items: flex-start; background: rgb(var(--color-surface));
          border: 1px solid rgb(var(--color-border)); border-radius: 8px;
          cursor: pointer; display: flex; gap: 8px; padding: 12px;
        }
        .settlement-radio-card[data-selected="true"] { background: rgb(var(--color-selected-bg)); }
        .settlement-grid {
          display: grid; gap: 16px; grid-template-columns: repeat(2, minmax(0, 1fr));
        }
        .settlement-field--full { grid-column: 1 / -1; }
        .settlement-input, .settlement-select, .settlement-textarea {
          background: rgb(var(--color-surface)); border: 1px solid rgb(var(--color-border));
          border-radius: 6px; box-sizing: border-box; color: rgb(var(--color-text));
          font: inherit; width: 100%;
        }
        .settlement-input, .settlement-select { min-height: 40px; padding: 8px 10px; }
        .settlement-textarea { min-height: 112px; padding: 10px; resize: vertical; }
        .settlement-input[aria-invalid="true"],
        .settlement-select[aria-invalid="true"],
        .settlement-textarea[aria-invalid="true"] { border-color: rgb(var(--color-danger)); }
        .settlement-checks { display: flex; flex-direction: column; gap: 10px; }
        .settlement-checkbox { align-items: flex-start; display: flex; gap: 8px; }
        .settlement-error { color: rgb(var(--color-danger)); font-size: 12px; margin: 5px 0 0; }
        .settlement-display-grid {
          display: grid; gap: 16px; grid-template-columns: repeat(2, minmax(0, 1fr));
        }
        .settlement-display-value { font-size: 14px; font-weight: 500; line-height: 1.5; margin: 0; }
        .settlement-section .modifier-manager-info,
        .settlement-section .modifier-manager-readonly-note {
          border: 1px solid rgb(var(--color-border)); border-radius: 8px;
          font-size: 13px; line-height: 1.5; margin: 0; padding: 10px 12px;
        }
        .settlement-section .modifier-manager-info { background: rgb(var(--color-info) / 0.08); }
        .settlement-section .modifier-manager-readonly-note {
          background: rgb(var(--color-surface-muted)); color: rgb(var(--color-text-muted));
        }
        @media (max-width: 720px) {
          .settlement-policy-options, .settlement-grid, .settlement-display-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      <header className="modifier-manager-header">
        <h2 id="settlement-heading" className="settlement-heading">Settlement</h2>
        <p className="settlement-subtitle">
          Configure how previously processed payouts are reconciled when later
          authoritative results require settlement.
        </p>
      </header>

      <div className="modifier-manager-body">
        {readOnly && (
          <p className="modifier-manager-readonly-note" role="note">
            This settlement configuration is read-only for this plan version.
            Changes require a successor amendment.
          </p>
        )}

        {readOnly ? (
          <>
            <div className="settlement-display-grid">
              <div>
                <span className="settlement-readonly-label">Settlement policy</span>
                <p className="settlement-display-value">{toLabel(POLICY_OPTIONS, form.policy)}</p>
              </div>

              {form.policy === 'true_up' && (
                <>
                  <div>
                    <span className="settlement-readonly-label">True-up frequency</span>
                    <p className="settlement-display-value">{toLabel(FREQUENCY_OPTIONS, form.trueUpFrequency)}</p>
                  </div>
                  <div>
                    <span className="settlement-readonly-label">Comparison basis</span>
                    <p className="settlement-display-value">{toLabel(COMPARISON_OPTIONS, form.comparisonBasis)}</p>
                  </div>
                  <div>
                    <span className="settlement-readonly-label">Lookback window</span>
                    <p className="settlement-display-value">
                      {form.lookbackWindowMonths == null
                        ? 'Not specified'
                        : `${form.lookbackWindowMonths} month${form.lookbackWindowMonths === 1 ? '' : 's'}`}
                    </p>
                  </div>
                  <div>
                    <span className="settlement-readonly-label">Overpayment recovery</span>
                    <p className="settlement-display-value">{rules.overpaymentRecovery ? 'Enabled' : 'Disabled'}</p>
                  </div>
                  <div>
                    <span className="settlement-readonly-label">Underpayment catch-up</span>
                    <p className="settlement-display-value">{rules.underpaymentCatchUp ? 'Enabled' : 'Disabled'}</p>
                  </div>
                  <div>
                    <span className="settlement-readonly-label">Recovery period</span>
                    <p className="settlement-display-value">
                      {rules.recoveryPeriodMonths == null
                        ? 'Not specified'
                        : `${rules.recoveryPeriodMonths} month${rules.recoveryPeriodMonths === 1 ? '' : 's'}`}
                    </p>
                  </div>
                  <div>
                    <span className="settlement-readonly-label">Year-end treatment</span>
                    <p className="settlement-display-value">{toLabel(YEAR_END_OPTIONS, form.yearEndTreatment)}</p>
                  </div>
                </>
              )}

              {form.policy === 'custom' && (
                <div className="settlement-field--full">
                  <span className="settlement-readonly-label">Custom settlement terms</span>
                  <p className="settlement-display-value">{form.customTerms || 'Not specified'}</p>
                </div>
              )}
            </div>

            {form.policy === 'none' && (
              <p className="modifier-manager-info" role="note">
                No settlement mechanism configured. Calculated payouts are final when processed.
              </p>
            )}
          </>
        ) : (
          <>
            <fieldset className="settlement-fieldset">
              <legend className="settlement-legend">Settlement policy</legend>
              <div className="settlement-policy-options">
                {POLICY_OPTIONS.map((option) => (
                  <label
                    key={option.value}
                    className="settlement-radio-card"
                    data-selected={form.policy === option.value ? 'true' : 'false'}
                  >
                    <input
                      type="radio"
                      name="settlement-policy"
                      value={option.value}
                      checked={form.policy === option.value}
                      onChange={(e) => handlePolicyChange(e.target.value)}
                    />
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>
              {renderError('policy')}
            </fieldset>

            {form.policy === 'none' && (
              <p className="modifier-manager-info" role="note">
                No settlement mechanism configured. Calculated payouts are final when processed.
              </p>
            )}

            {form.policy === 'true_up' && (
              <div className="settlement-grid">
                <div>
                  <label className="settlement-label" htmlFor="settlement-frequency">
                    True-up frequency
                  </label>
                  <select
                    id="settlement-frequency"
                    className="settlement-select"
                    value={form.trueUpFrequency ?? ''}
                    onChange={(e) => setField('trueUpFrequency', e.target.value || null)}
                    aria-invalid={Boolean(errorFor('trueUpFrequency'))}
                    aria-describedby={describedBy('trueUpFrequency')}
                  >
                    <option value="">Select frequency</option>
                    {FREQUENCY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  {renderError('trueUpFrequency')}
                </div>

                <div>
                  <label className="settlement-label" htmlFor="settlement-comparison-basis">
                    Comparison basis
                  </label>
                  <select
                    id="settlement-comparison-basis"
                    className="settlement-select"
                    value={form.comparisonBasis ?? ''}
                    onChange={(e) => setField('comparisonBasis', e.target.value || null)}
                    aria-invalid={Boolean(errorFor('comparisonBasis'))}
                    aria-describedby={describedBy('comparisonBasis')}
                  >
                    <option value="">Select comparison basis</option>
                    {COMPARISON_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  {renderError('comparisonBasis')}
                </div>

                <div>
                  <label className="settlement-label" htmlFor="settlement-lookback">
                    Lookback window (months)
                  </label>
                  <input
                    id="settlement-lookback"
                    className="settlement-input"
                    type="number" min="1" step="1" inputMode="numeric"
                    value={form.lookbackWindowMonths ?? ''}
                    onChange={(e) => setField('lookbackWindowMonths', e.target.value === '' ? null : Number(e.target.value))}
                    aria-invalid={Boolean(errorFor('lookbackWindowMonths'))}
                    aria-describedby={describedBy('lookbackWindowMonths')}
                  />
                  {renderError('lookbackWindowMonths')}
                </div>

                <fieldset className="settlement-fieldset">
                  <legend className="settlement-legend">Carry-forward rules</legend>
                  <div className="settlement-checks">
                    <label className="settlement-checkbox">
                      <input
                        type="checkbox"
                        checked={rules.overpaymentRecovery}
                        onChange={(e) => setCarryForward('overpaymentRecovery', e.target.checked)}
                      />
                      <span>Recover overpayments</span>
                    </label>
                    <label className="settlement-checkbox">
                      <input
                        type="checkbox"
                        checked={rules.underpaymentCatchUp}
                        onChange={(e) => setCarryForward('underpaymentCatchUp', e.target.checked)}
                      />
                      <span>Catch up underpayments</span>
                    </label>
                  </div>
                </fieldset>

                {rules.overpaymentRecovery && (
                  <div>
                    <label className="settlement-label" htmlFor="settlement-recovery-period">
                      Recovery period (months)
                    </label>
                    <input
                      id="settlement-recovery-period"
                      className="settlement-input"
                      type="number" min="1" step="1" inputMode="numeric"
                      value={rules.recoveryPeriodMonths ?? ''}
                      onChange={(e) => setCarryForward('recoveryPeriodMonths', e.target.value === '' ? null : Number(e.target.value))}
                      aria-invalid={Boolean(errorFor('carryForwardRules.recoveryPeriodMonths'))}
                      aria-describedby={describedBy('carryForwardRules.recoveryPeriodMonths')}
                    />
                    {renderError('carryForwardRules.recoveryPeriodMonths')}
                  </div>
                )}

                <div>
                  <label className="settlement-label" htmlFor="settlement-year-end">
                    Year-end treatment
                  </label>
                  <select
                    id="settlement-year-end"
                    className="settlement-select"
                    value={form.yearEndTreatment ?? ''}
                    onChange={(e) => setField('yearEndTreatment', e.target.value || null)}
                    aria-invalid={Boolean(errorFor('yearEndTreatment'))}
                    aria-describedby={describedBy('yearEndTreatment')}
                  >
                    <option value="">Select treatment</option>
                    {YEAR_END_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  {renderError('yearEndTreatment')}
                </div>
              </div>
            )}

            {form.policy === 'custom' && (
              <div>
                <label className="settlement-label" htmlFor="settlement-custom-terms">
                  Custom settlement terms
                </label>
                <textarea
                  id="settlement-custom-terms"
                  className="settlement-textarea"
                  value={form.customTerms ?? ''}
                  onChange={(e) => setField('customTerms', e.target.value || null)}
                  aria-invalid={Boolean(errorFor('customTerms'))}
                  aria-describedby={describedBy('customTerms')}
                />
                {renderError('customTerms')}
              </div>
            )}
          </>
        )}

        <p className="modifier-manager-info" role="note">
          Looking for non-cash awards? Configure those in Payout Adjustments.
        </p>
      </div>
    </section>
  );
});

SettlementSection.displayName = 'SettlementSection';

SettlementSection.propTypes = {
  plan: PropTypes.shape({
    planId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  settlementConfig: PropTypes.shape({
    policy:               PropTypes.oneOf(['none', 'true_up', 'custom']).isRequired,
    trueUpFrequency:      PropTypes.oneOf(['monthly', 'quarterly', 'annual', 'on_demand']),
    comparisonBasis:      PropTypes.oneOf(['provisional_vs_authoritative', 'custom']),
    lookbackWindowMonths: PropTypes.number,
    carryForwardRules:    PropTypes.shape({
      overpaymentRecovery:  PropTypes.bool.isRequired,
      underpaymentCatchUp:  PropTypes.bool.isRequired,
      recoveryPeriodMonths: PropTypes.number,
    }),
    yearEndTreatment: PropTypes.oneOf(['reset', 'carry_forward', 'lump_sum']),
    customTerms:      PropTypes.string,
  }).isRequired,
  readOnly:         PropTypes.bool.isRequired,
  validationErrors: PropTypes.objectOf(PropTypes.string),
};

export default SettlementSection;
